<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Search, ArrowRight, Users, BookOpen } from 'lucide-vue-next'
import { searchApi, type GlobalSearchResponse } from '@/api'
import { useAppShellStore } from '@/store/AppShellStore'
import { paletteForKey, paletteFromColour, type PaletteColour, type PaletteHue } from '@/design-system/components'

/**
 * CommandPalette (§02) — ⌘K / top-bar search. Centred overlay over a scrim,
 * live-filtered sectioned results (Classes / Students / Schemes) from the real
 * /search API. Empty query shows the loaded classes as recents. Esc / outside
 * click closes; Enter activates the first result.
 */
const router = useRouter()
const appShell = useAppShellStore()

const open = computed(() => appShell.paletteOpen)
const query = ref('')
const loading = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)
const results = ref<GlobalSearchResponse>({ query: '', students: [], classes: [], planningSchemes: [], assignments: [] })

let debounce: ReturnType<typeof setTimeout> | null = null

interface Jump {
  route: string
}
type Hue = PaletteHue

function hueFor(colour?: string, key?: string): Hue {
  if (colour) return paletteFromColour(colour as PaletteColour)
  return paletteForKey(key ?? '')
}

// On empty query, surface the loaded classes as recents.
const emptyClasses = computed(() =>
  appShell.sortedClasses.map((c) => ({
    id: c.id,
    name: c.name,
    colour: c.colour ?? undefined,
    studentCount: c.studentCount,
  })),
)

const shownClasses = computed(() =>
  query.value.trim() ? (results.value.classes ?? []) : emptyClasses.value,
)
const shownStudents = computed(() => (query.value.trim() ? (results.value.students ?? []) : []))
const shownSchemes = computed(() =>
  query.value.trim() ? (results.value.planningSchemes ?? []) : [],
)

const totalShown = computed(
  () => shownClasses.value.length + shownStudents.value.length + shownSchemes.value.length,
)

// Flat ordered list (Classes → Students → Schemes) for Enter = first result.
const firstJump = computed<Jump | null>(() => {
  const c = shownClasses.value[0]
  if (c?.id) return { route: `/classes/${c.id}` }
  const s = shownStudents.value[0]
  if (s?.id) return { route: `/students/${s.id}` }
  const sc = shownSchemes.value[0]
  if (sc?.id) return { route: `/planning/${sc.id}` }
  return null
})

function studentName(s: { displayName?: string; firstName?: string; lastName?: string }): string {
  if (s.lastName || s.firstName) return `${s.lastName ?? ''}, ${s.firstName ?? ''}`.replace(/^, |, $/g, '')
  return s.displayName ?? 'Unknown'
}
function studentInitials(s: { firstName?: string; lastName?: string; displayName?: string }): string {
  const f = s.firstName?.[0] ?? s.displayName?.[0] ?? ''
  const l = s.lastName?.[0] ?? ''
  return (f + l).toUpperCase() || '?'
}

async function runSearch() {
  const q = query.value.trim()
  if (!q) {
    results.value = { query: '', students: [], classes: [], planningSchemes: [], assignments: [] }
    loading.value = false
    return
  }
  loading.value = true
  try {
    results.value = await searchApi.search(q)
  } catch {
    results.value = { query: '', students: [], classes: [], planningSchemes: [], assignments: [] }
  } finally {
    loading.value = false
  }
}

watch(query, () => {
  if (debounce) clearTimeout(debounce)
  if (!query.value.trim()) {
    runSearch()
    return
  }
  debounce = setTimeout(runSearch, 220)
})

watch(open, (isOpen) => {
  if (isOpen) {
    query.value = ''
    results.value = { query: '', students: [], classes: [], planningSchemes: [], assignments: [] }
    nextTick(() => inputRef.value?.focus())
  }
})

function close() {
  appShell.closePalette()
}
function jump(route: string) {
  close()
  router.push(route)
}
function onEnter() {
  if (firstJump.value) jump(firstJump.value.route)
}
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close()
}
</script>

<template>
  <div v-if="open" class="cp" @mousedown.self="close" @keydown="onKeydown">
    <div class="cp__panel" role="dialog" aria-label="Search">
      <div class="cp__search">
        <Search :size="19" class="cp__search-icon" />
        <input
          ref="inputRef"
          v-model="query"
          class="cp__input"
          type="text"
          placeholder="Search classes, students, schemes…"
          @keydown.enter.prevent="onEnter"
          @keydown.esc.prevent="close"
        />
        <span class="cp__esc">ESC</span>
      </div>

      <div class="cp__results">
        <!-- Classes -->
        <template v-if="shownClasses.length">
          <div class="cp__sec">
            <span class="cp__sec-label">Classes</span>
            <span class="cp__sec-count">{{ shownClasses.length }}</span>
          </div>
          <button
            v-for="c in shownClasses"
            :key="'c' + c.id"
            type="button"
            class="cp__row"
            @click="jump(`/classes/${c.id}`)"
          >
            <span
              class="cp__tile"
              :style="{ background: hueFor(c.colour, c.id).soft, color: hueFor(c.colour, c.id).ink }"
            >
              <Users :size="17" />
            </span>
            <span class="cp__row-text">
              <span class="cp__row-title">{{ c.name }}</span>
              <span class="cp__row-meta">{{ c.studentCount ?? 0 }} students</span>
            </span>
            <ArrowRight :size="15" class="cp__row-arrow" />
          </button>
        </template>

        <!-- Students -->
        <template v-if="shownStudents.length">
          <div class="cp__sec">
            <span class="cp__sec-label">Students</span>
            <span class="cp__sec-count">{{ shownStudents.length }}</span>
          </div>
          <button
            v-for="s in shownStudents"
            :key="'s' + s.id"
            type="button"
            class="cp__row"
            @click="jump(`/students/${s.id}`)"
          >
            <span
              class="cp__avatar"
              :style="{ background: hueFor(undefined, s.id).soft, color: hueFor(undefined, s.id).ink }"
            >
              {{ studentInitials(s) }}
            </span>
            <span class="cp__row-text">
              <span class="cp__row-title">{{ studentName(s) }}</span>
              <span v-if="s.schoolId" class="cp__row-meta">{{ s.schoolId }}</span>
            </span>
            <ArrowRight :size="15" class="cp__row-arrow" />
          </button>
        </template>

        <!-- Schemes -->
        <template v-if="shownSchemes.length">
          <div class="cp__sec">
            <span class="cp__sec-label">Schemes of work</span>
            <span class="cp__sec-count">{{ shownSchemes.length }}</span>
          </div>
          <button
            v-for="sc in shownSchemes"
            :key="'sc' + sc.id"
            type="button"
            class="cp__row"
            @click="jump(`/planning/${sc.id}`)"
          >
            <span
              class="cp__tile"
              :style="{ background: hueFor(sc.colour, sc.id).soft, color: hueFor(sc.colour, sc.id).ink }"
            >
              <BookOpen :size="17" />
            </span>
            <span class="cp__row-text">
              <span class="cp__row-title">{{ sc.name }}</span>
              <span class="cp__row-meta">
                {{ sc.courseName ? sc.courseName + ' · ' : '' }}{{ sc.unitCount ?? 0 }} units
              </span>
            </span>
            <ArrowRight :size="15" class="cp__row-arrow" />
          </button>
        </template>

        <div v-if="query.trim() && !loading && totalShown === 0" class="cp__empty">
          No matches for “{{ query.trim() }}”.
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cp {
  position: fixed;
  inset: 0;
  z-index: var(--z-tooltip);
  background: rgba(15, 15, 20, 0.28);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 13vh;
}
.cp__panel {
  width: 580px;
  max-width: 92vw;
  background: var(--surface-raised);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-modal);
  overflow: hidden;
  font-family: var(--font-sans);
}
.cp__search {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 18px;
  border-bottom: 1px solid var(--hairline);
}
.cp__search-icon {
  color: var(--ink-400);
  flex-shrink: 0;
}
.cp__input {
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: 16px;
  letter-spacing: -0.2px;
  color: var(--text-primary);
}
.cp__input::placeholder {
  color: var(--ink-400);
}
.cp__esc {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.6px;
  color: var(--ink-400);
  padding: 3px 6px;
  border-radius: var(--radius-xs);
  box-shadow: inset 0 0 0 1px var(--ink-200);
}
.cp__results {
  max-height: 52vh;
  overflow-y: auto;
  padding-bottom: 8px;
}
.cp__sec {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 6px;
}
.cp__sec-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--ink-400);
}
.cp__sec-count {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  color: var(--ink-400);
}
.cp__row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 9px 16px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}
.cp__row:hover {
  background: var(--ink-50);
}
.cp__tile,
.cp__avatar {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cp__tile {
  border-radius: 9px;
}
.cp__avatar {
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
}
.cp__row-text {
  flex: 1;
  min-width: 0;
}
.cp__row-title {
  display: block;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.1px;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cp__row-meta {
  display: block;
  margin-top: 1px;
  font-size: 12px;
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cp__row-arrow {
  color: var(--ink-300);
  flex-shrink: 0;
}
.cp__empty {
  padding: 38px 16px;
  text-align: center;
  color: var(--ink-400);
  font-size: 14px;
}
</style>
